import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  BarChart3,
  Clock,
  Download,
  Eye,
  Mail,
  Send,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  Users,
  Wand2,
  Home,
  DatabaseZap,
  Trash2,
  RotateCcw,
  AlertTriangle,
  Filter,
  Lock,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Progress } from "../components/ui/progress";
import NudgeComposer from "../components/NudgeComposer";
import LaunchEmailBlastButton from "../components/LaunchEmailBlastButton";
import AdminEmailBlastPanel from "../components/AdminEmailBlastPanel";
import DeleteRowButton from "../components/DeleteRowButton";
import TierOverrideDropdown from "../components/TierOverrideDropdown";
import RetentionUsagePanel from "../components/RetentionUsagePanel";
import PostersFunnelPanel from "../components/admin/PostersFunnelPanel";
import ReflectionAggregatePanel from "../components/admin/ReflectionAggregatePanel";
import LlmUsagePanel from "../components/admin/LlmUsagePanel";
import HomepageHeroPanel from "../components/admin/HomepageHeroPanel";
import ReportCardNudgePanel from "../components/admin/ReportCardNudgePanel";
import FeaturedLessonPanel from "../components/admin/FeaturedLessonPanel";
import PackageCachePanel from "../components/admin/PackageCachePanel";
import { useAuth } from "../contexts/AuthContext";
import {
  adminDeleteEarlyAccess,
  adminDeleteLaunchWaitlist,
  adminEarlyAccess,
  adminEarlyAccessCsvUrl,
  adminGlobalCoverage,
  adminAutoPickStats,
  adminGrantMathAccess,
  adminLaunchWaitlist,
  adminLaunchWaitlistCsvUrl,
  adminRecentLessons,
  adminSummary,
  adminTeachers,
  adminTeachersCsvUrl,
  adminPreviewDeleteUsers,
  adminDeleteUsers,
  adminRecentlyDeletedUsers,
  adminRestoreUsers,
  adminPurgeUsersNow,
} from "../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const StatCard = ({ icon: Icon, label, value, sub, testid, tone = "default" }) => (
  <div
    data-testid={testid}
    className={`ae-card p-6 ${
      tone === "accent"
        ? "border-[#96ded1]/60 bg-[#96ded1]/15"
        : ""
    }`}
  >
    <div className="flex items-center gap-3">
      <div
        className={`h-10 w-10 rounded-xl grid place-items-center ${
          tone === "accent"
            ? "bg-white border border-[#96ded1]/60"
            : "bg-[#f4f5f7] border border-[#4a6984]/15"
        }`}
      >
        <Icon className="h-4 w-4 text-[#4a6984]" strokeWidth={2} />
      </div>
      <span className="font-bold uppercase text-xs tracking-[0.22em] text-[#4a6984]/70">
        {label}
      </span>
    </div>
    <p className="mt-4 font-black text-3xl text-[#4a6984] tracking-tight">{value}</p>
    {sub && (
      <p className="mt-1 text-xs font-semibold text-[#4a6984]/70">{sub}</p>
    )}
  </div>
);

const sortDesc = (obj) =>
  Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);

const sortAsc = (obj) =>
  Object.entries(obj || {}).sort((a, b) => a[1] - b[1]);

const CoverageBars = ({ title, data, total, tone = "default", emptyHint, testid }) => {
  const rows = sortDesc(data);
  const max = rows.length ? rows[0][1] : 1;
  const barCls =
    tone === "gap"
      ? "[&>div]:bg-[#f0a17a]"
      : "[&>div]:bg-[#96ded1]";
  return (
    <div className="ae-card p-6" data-testid={testid}>
      <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight mb-4">
        {title}
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-[#4a6984]/60">{emptyHint || "No data yet."}</p>
      ) : (
        <ul className="space-y-3">
          {rows.map(([name, count]) => (
            <li key={name}>
              <div className="flex items-center justify-between mb-1.5 gap-3">
                <span className="text-sm font-semibold text-[#4a6984] truncate">
                  {name}
                </span>
                <span className="text-xs font-bold text-[#4a6984]/70">
                  {count}
                  {total ? ` / ${total}` : ""}
                </span>
              </div>
              <Progress
                value={(count / max) * 100}
                className={`h-2 bg-[#f4f5f7] ${barCls}`}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// "Push opportunities" — find the bottom-5 most-rarely-used items across the
// Ontario learning skills + transferable skills + curriculum links. These
// are the ones the admin can nudge teachers about.
const buildGapList = (coverage) => {
  const merged = {};
  for (const [k, v] of Object.entries(coverage?.by_learning_skill || {})) {
    merged[`Learning skill — ${k}`] = (merged[`Learning skill — ${k}`] || 0) + v;
  }
  for (const [k, v] of Object.entries(coverage?.by_transferable_skill || {})) {
    merged[`Transferable skill — ${k}`] = (merged[`Transferable skill — ${k}`] || 0) + v;
  }
  for (const [k, v] of Object.entries(coverage?.by_skill_focus || {})) {
    merged[`Skill focus — ${k}`] = (merged[`Skill focus — ${k}`] || 0) + v;
  }
  for (const [k, v] of Object.entries(coverage?.by_strand || {})) {
    merged[`Strand — ${k}`] = (merged[`Strand — ${k}`] || 0) + v;
  }
  // Bottom 8 — rarely-used items
  return sortAsc(merged).slice(0, 8);
};

export const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const [summary, setSummary] = useState(null);
  const [signups, setSignups] = useState(null);
  const [waitlist, setWaitlist] = useState(null);
  const [teachers, setTeachers] = useState(null);
  const [lessons, setLessons] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [autoPick, setAutoPick] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgeGap, setNudgeGap] = useState(null); // active gap-row for the nudge composer

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) return;
    let abort = false;
    (async () => {
      setLoading(true);
      try {
        const [s, e, w, t, r, c, ap] = await Promise.all([
          adminSummary(),
          adminEarlyAccess(),
          adminLaunchWaitlist(),
          adminTeachers(),
          adminRecentLessons(50),
          adminGlobalCoverage(),
          adminAutoPickStats(),
        ]);
        if (abort) return;
        setSummary(s);
        setSignups(e);
        setWaitlist(w);
        setTeachers(t);
        setLessons(r);
        setCoverage(c);
        setAutoPick(ap);
      } catch (err) {
        if (!abort) {
          toast.error("Could not load admin data", {
            description: err?.response?.data?.detail || err?.message,
          });
        }
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [authLoading, user]);

  const gapList = useMemo(() => buildGapList(coverage), [coverage]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] grid place-items-center text-[#4a6984]/70">
        Checking access…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/" replace />;
  }
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] text-[#4a6984] grid place-items-center">
        <div className="ae-card p-10 max-w-md text-center" data-testid="admin-forbidden">
          <ShieldCheck className="h-6 w-6 mx-auto text-[#1c2d3d] mb-3" />
          <h2 className="font-extrabold text-xl">Admin only</h2>
          <p className="mt-2 text-[#4a6984]/80">
            This area is restricted. Head back to your dashboard.
          </p>
          <Link
            to="/dashboard"
            className="ae-cta mt-6 inline-flex items-center px-6 py-3 rounded-lg text-sm"
          >
            Go to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const total = summary?.total_lessons ?? 0;

  return (
    <div className="bg-[#f4f5f7] text-[#4a6984] overflow-x-clip">
      <main className="py-8 max-w-7xl mx-auto px-6 min-w-0">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
              Admin
            </p>
            <h1 className="mt-2 font-black text-3xl sm:text-4xl text-[#4a6984] tracking-tight">
              Atomic Edu, behind the scenes
            </h1>
            <p className="mt-3 text-[#4a6984]/80 font-medium max-w-2xl">
              Signups, usage, what teachers are making, and where curriculum coverage is
              thin enough to be worth nudging.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/admin/render-preview"
              data-testid="admin-render-preview-link"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#96ded1]/30 hover:bg-[#96ded1]/45 border border-[#1c9e7e]/45 text-[#1c2d3d] font-extrabold text-xs uppercase tracking-[0.12em] transition-colors"
            >
              <Eye className="h-3.5 w-3.5" />
              Render Preview
            </Link>
            <Link
              to="/admin/spec-sheet"
              data-testid="admin-spec-sheet-link"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#96ded1]/30 hover:bg-[#96ded1]/45 border border-[#1c9e7e]/45 text-[#1c2d3d] font-extrabold text-xs uppercase tracking-[0.12em] transition-colors"
            >
              <span className="text-[#1c9e7e]">📘</span>
              Product Spec Sheet
            </Link>
          </div>
        </div>

        {loading ? (
          <p className="text-[#4a6984]/70">Loading admin data…</p>
        ) : (
          <>
            {/* TOP STATS */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <StatCard
                icon={Sparkles}
                label="Lessons generated"
                value={summary?.total_lessons ?? 0}
                sub={`${summary?.lessons_last_7_days ?? 0} in the last 7 days`}
                testid="stat-total-lessons"
              />
              <StatCard
                icon={Sparkles}
                label="Lessons today"
                value={summary?.lessons_today ?? 0}
                testid="stat-lessons-today"
                tone="accent"
              />
              <StatCard
                icon={Users}
                label="Signed-up teachers"
                value={summary?.signed_up_teachers ?? 0}
                testid="stat-teachers"
              />
              <StatCard
                icon={Mail}
                label="Early-access signups"
                value={summary?.early_access_signups ?? 0}
                testid="stat-early-access"
              />
              <StatCard
                icon={Clock}
                label="Launch waitlist"
                value={summary?.launch_waitlist_signups ?? 0}
                testid="stat-launch-waitlist"
                tone="accent"
              />
            </div>

            <Tabs defaultValue="signups" className="w-full min-w-0">
              {/* Feb 2026 — Mobile responsiveness fix (approach A).
                  The shadcn <TabsList> is `inline-flex` with per-trigger
                  `whitespace-nowrap`; on a 390 px viewport the 11 tabs
                  form a ~1686 px strip that used to push the entire
                  page canvas to 1710 px, forcing horizontal page scroll
                  AND making <TabsContent> render inside a
                  wider-than-viewport box (the "narrow left column"
                  symptom).
                  Fix: wrap the strip in an overflow-x-auto container
                  with a mobile-only mint scroll-hint on the right edge
                  so no tab is missed. `overflow-x-clip` on the outer
                  admin container is the belt-and-suspenders guard that
                  keeps any future wide descendant from repeating the
                  bug. Desktop is unchanged — the strip fits within the
                  content width and the hint is `md:hidden`. */}
              <div className="relative -mx-6 md:mx-0" data-testid="admin-tabs-scroll-wrap">
                <div
                  className="overflow-x-auto px-6 md:px-0 min-w-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                  data-testid="admin-tabs-scroller"
                >
                  <TabsList
                    className="bg-white border border-[#4a6984]/10 p-1 mb-8"
                    data-testid="admin-tabs"
                  >
                <TabsTrigger
                  value="signups"
                  data-testid="tab-signups"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <Mail className="h-3.5 w-3.5 mr-1.5" /> Early access
                </TabsTrigger>
                <TabsTrigger
                  value="waitlist"
                  data-testid="tab-waitlist"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <Clock className="h-3.5 w-3.5 mr-1.5" /> Launch waitlist
                </TabsTrigger>
                <TabsTrigger
                  value="teachers"
                  data-testid="tab-teachers"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <Users className="h-3.5 w-3.5 mr-1.5" /> Signed-up teachers
                </TabsTrigger>
                <TabsTrigger
                  value="recent"
                  data-testid="tab-recent"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Recent lessons
                </TabsTrigger>
                <TabsTrigger
                  value="trends"
                  data-testid="tab-trends"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <TrendingDown className="h-3.5 w-3.5 mr-1.5" /> Trends & gaps
                </TabsTrigger>
                <TabsTrigger
                  value="retention"
                  data-testid="tab-retention"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Retention & Usage
                </TabsTrigger>
                <TabsTrigger
                  value="email"
                  data-testid="tab-email"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Email all users
                </TabsTrigger>
                <TabsTrigger
                  value="posters"
                  data-testid="tab-posters"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Posters funnel
                </TabsTrigger>
                <TabsTrigger
                  value="reflection"
                  data-testid="tab-reflection"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Reflection survey
                </TabsTrigger>
                <TabsTrigger
                  value="llm-usage"
                  data-testid="tab-llm-usage"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> LLM usage
                </TabsTrigger>
                <TabsTrigger
                  value="homepage"
                  data-testid="tab-homepage"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <Home className="h-3.5 w-3.5 mr-1.5" /> Homepage
                </TabsTrigger>
                <TabsTrigger
                  value="featured-lesson"
                  data-testid="tab-featured-lesson"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Featured Lesson
                </TabsTrigger>
                <TabsTrigger
                  value="maintenance"
                  data-testid="tab-maintenance"
                  className="data-[state=active]:bg-[#4a6984] data-[state=active]:text-white text-[#4a6984] font-semibold"
                >
                  <DatabaseZap className="h-3.5 w-3.5 mr-1.5" /> Maintenance
                </TabsTrigger>
              </TabsList>
                </div>
                {/* Mobile-only right-edge scroll hint (soft mint). Static
                    gradient — always visible on mobile so a first-time
                    visitor knows to swipe. Hidden ≥md where the strip
                    fits without scrolling. */}
                <div
                  aria-hidden="true"
                  data-testid="admin-tabs-scroll-hint"
                  className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#96ded1]/70 via-[#96ded1]/25 to-transparent md:hidden"
                />
              </div>

              {/* EARLY ACCESS */}
              <TabsContent value="signups" className="space-y-4">
                <div className="ae-card p-6 md:p-7" data-testid="admin-signups-card">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                    <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
                      Early access signups ({signups?.count ?? 0})
                    </h3>
                    <a
                      href={adminEarlyAccessCsvUrl()}
                      data-testid="admin-signups-csv"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] font-semibold text-sm hover:bg-[#f4f5f7] hover:border-[#4a6984]/35 transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" /> Download CSV
                    </a>
                  </div>
                  {(signups?.signups || []).length === 0 ? (
                    <p className="text-sm text-[#4a6984]/60">
                      No signups yet — the form is on the home page footer.
                    </p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-[#4a6984]/10">
                      <table className="w-full text-sm">
                        <thead className="bg-[#f4f5f7]">
                          <tr>
                            <th className="text-left font-extrabold uppercase tracking-[0.12em] text-xs text-[#4a6984] p-3 border-b border-[#4a6984]/15">
                              Email
                            </th>
                            <th className="text-left font-extrabold uppercase tracking-[0.12em] text-xs text-[#4a6984] p-3 border-b border-[#4a6984]/15">
                              Signed up
                            </th>
                            <th className="p-3 border-b border-[#4a6984]/15" aria-label="actions" />
                          </tr>
                        </thead>
                        <tbody data-testid="admin-signups-rows">
                          {signups.signups.map((r) => (
                            <tr key={r.email}>
                              <td className="p-3 font-semibold text-[#4a6984] border-b border-[#4a6984]/10">
                                {r.email}
                              </td>
                              <td className="p-3 text-[#4a6984]/80 border-b border-[#4a6984]/10">
                                {fmtDate(r.created_at)}
                              </td>
                              <td className="p-3 border-b border-[#4a6984]/10 text-right">
                                <DeleteRowButton
                                  testid={`delete-early-access-${r.email}`}
                                  label="early-access entry"
                                  email={r.email}
                                  onConfirm={async () => {
                                    await adminDeleteEarlyAccess(r.email);
                                    setSignups((cur) => ({
                                      ...cur,
                                      count: Math.max(0, (cur?.count || 1) - 1),
                                      signups: (cur?.signups || []).filter(
                                        (x) => x.email !== r.email,
                                      ),
                                    }));
                                    toast.success(`Removed ${r.email}`);
                                  }}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* LAUNCH WAITLIST — captured during the pre-launch
                  period. Kept post-launch as a generic email-capture
                  tool the founder can blast announcements to. */}
              <TabsContent value="waitlist" className="space-y-4">
                <div className="ae-card p-6 md:p-7" data-testid="admin-waitlist-card">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                    <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
                      Launch waitlist ({waitlist?.count ?? 0})
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      <LaunchEmailBlastButton
                        onSent={async () => {
                          try {
                            const w = await adminLaunchWaitlist();
                            setWaitlist(w);
                          } catch {/* ignore */}
                        }}
                      />
                      <a
                        href={adminLaunchWaitlistCsvUrl()}
                        data-testid="admin-waitlist-csv"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] font-semibold text-sm hover:bg-[#f4f5f7] hover:border-[#4a6984]/35 transition-colors"
                      >
                        <Download className="h-3.5 w-3.5" /> Download CSV
                      </a>
                    </div>
                  </div>
                  <p className="text-xs text-[#4a6984]/70 font-medium mb-5">
                    Emails captured during the pre-launch period. Click
                    <span className="font-bold"> Email teachers</span> to
                    blast a launch announcement — safe to re-click, only
                    un-emailed teachers will receive it.
                  </p>
                  {(waitlist?.signups || []).length === 0 ? (
                    <p className="text-sm text-[#4a6984]/60">
                      No one on the waitlist.
                    </p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-[#4a6984]/10">
                      <table className="w-full text-sm">
                        <thead className="bg-[#f4f5f7]">
                          <tr>
                            <th className="text-left font-extrabold uppercase tracking-[0.12em] text-xs text-[#4a6984] p-3 border-b border-[#4a6984]/15">
                              Email
                            </th>
                            <th className="text-left font-extrabold uppercase tracking-[0.12em] text-xs text-[#4a6984] p-3 border-b border-[#4a6984]/15">
                              Source
                            </th>
                            <th className="text-left font-extrabold uppercase tracking-[0.12em] text-xs text-[#4a6984] p-3 border-b border-[#4a6984]/15">
                              Joined
                            </th>
                            <th className="text-left font-extrabold uppercase tracking-[0.12em] text-xs text-[#4a6984] p-3 border-b border-[#4a6984]/15">
                              Launch email
                            </th>
                            <th className="p-3 border-b border-[#4a6984]/15" aria-label="actions" />
                          </tr>
                        </thead>
                        <tbody data-testid="admin-waitlist-rows">
                          {waitlist.signups.map((r) => (
                            <tr key={r.email}>
                              <td className="p-3 font-semibold text-[#4a6984] border-b border-[#4a6984]/10">
                                {r.email}
                              </td>
                              <td className="p-3 text-[#4a6984]/80 border-b border-[#4a6984]/10">
                                {r.source || "pricing_page"}
                              </td>
                              <td className="p-3 text-[#4a6984]/80 border-b border-[#4a6984]/10">
                                {fmtDate(r.created_at)}
                              </td>
                              <td className="p-3 border-b border-[#4a6984]/10">
                                {r.sent_at ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-extrabold uppercase tracking-[0.1em]"
                                    title={fmtDate(r.sent_at)}
                                  >
                                    ✓ Sent
                                  </span>
                                ) : (
                                  <span className="text-[11px] text-[#4a6984]/50 font-medium uppercase tracking-[0.1em]">
                                    Pending
                                  </span>
                                )}
                              </td>
                              <td className="p-3 border-b border-[#4a6984]/10 text-right">
                                <DeleteRowButton
                                  testid={`delete-waitlist-${r.email}`}
                                  label="waitlist entry"
                                  email={r.email}
                                  onConfirm={async () => {
                                    await adminDeleteLaunchWaitlist(r.email);
                                    setWaitlist((cur) => ({
                                      ...cur,
                                      count: Math.max(0, (cur?.count || 1) - 1),
                                      signups: (cur?.signups || []).filter(
                                        (x) => x.email !== r.email,
                                      ),
                                    }));
                                    toast.success(`Removed ${r.email}`);
                                  }}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* SIGNED-UP TEACHERS — full account list, distinct from
                  early-access marketing waitlist. */}
              <TabsContent value="teachers" className="space-y-4">
                <TeachersTab isSuperAdmin={!!user?.is_super_admin} />
              </TabsContent>

              {/* RECENT LESSONS — anonymized */}
              <TabsContent value="recent" className="space-y-4">
                <div className="ae-card p-6 md:p-7" data-testid="admin-recent-card">
                  <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight mb-1">
                    Recent lessons ({lessons?.count ?? 0})
                  </h3>
                  <p className="text-sm text-[#4a6984]/70 mb-5">
                    Anonymized — no teacher email. Just what&apos;s being made and when.
                  </p>
                  {(lessons?.lessons || []).length === 0 ? (
                    <p className="text-sm text-[#4a6984]/60">No lessons yet.</p>
                  ) : (
                    <ul className="space-y-3" data-testid="admin-recent-rows">
                      {lessons.lessons.map((l) => {
                        const ip = l.inputs || {};
                        return (
                          <li
                            key={l.id}
                            className="rounded-lg border border-[#4a6984]/10 p-4 bg-[#f4f5f7]/60"
                          >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="min-w-0">
                                <p className="font-extrabold text-[#4a6984] tracking-tight truncate">
                                  {l.activity_name}
                                </p>
                                {l.tagline && (
                                  <p className="text-sm text-[#4a6984]/80 line-clamp-1">
                                    {l.tagline}
                                  </p>
                                )}
                              </div>
                              <span className="text-xs font-bold text-[#4a6984]/70 whitespace-nowrap">
                                {fmtDate(l.created_at)}
                              </span>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {ip.grade && <span className="ae-pill">{ip.grade}</span>}
                              {ip.subject && <span className="ae-pill">{ip.subject}</span>}
                              {ip.time && <span className="ae-pill">{ip.time}</span>}
                              {ip.activity_type && (
                                <span className="ae-pill">{ip.activity_type}</span>
                              )}
                              {ip.skill_focus && (
                                <span className="ae-pill !bg-[#96ded1]/25 !border-[#96ded1]/60 !text-[#1c2d3d]">
                                  {ip.skill_focus}
                                </span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </TabsContent>

              {/* TRENDS / GAPS */}
              <TabsContent value="trends" className="space-y-6">
                <div className="ae-card p-6 md:p-7 border-[#f0a17a]/40 bg-[#fff7f0]" data-testid="admin-gap-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-9 w-9 rounded-lg bg-white border border-[#f0a17a]/50 grid place-items-center">
                      <TrendingDown className="h-4 w-4 text-[#b75d2c]" />
                    </div>
                    <div>
                      <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
                        Push opportunities
                      </h3>
                      <p className="text-sm text-[#4a6984]/75">
                        Skills, learning skills, and skill-focus picks that are
                        under-represented across all teachers — good candidates to
                        nudge teachers toward.
                      </p>
                    </div>
                  </div>
                  {gapList.length === 0 ? (
                    <p className="text-sm text-[#4a6984]/60">
                      Once a few lessons are generated, gaps will show up here.
                    </p>
                  ) : (
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="admin-gap-rows">
                      {gapList.map(([name, count]) => (
                        <li
                          key={name}
                          className="flex items-center justify-between gap-2 rounded-md bg-white border border-[#f0a17a]/30 px-3 py-2"
                        >
                          <span className="text-sm font-semibold text-[#4a6984] truncate">
                            {name}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs font-bold text-[#b75d2c]">
                              {count} {count === 1 ? "lesson" : "lessons"}
                            </span>
                            <button
                              type="button"
                              onClick={() => setNudgeGap(name)}
                              data-testid={`admin-nudge-${name.slice(0, 24).replace(/[^a-zA-Z0-9]+/g, "-")}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#1c2d3d] text-white text-[10px] font-extrabold uppercase tracking-wider hover:bg-[#1c2d3d]/90"
                            >
                              <Mail className="h-3 w-3" /> Nudge
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Tier 2 #4 (Feb 2026) — Auto-pick adoption telemetry. */}
                <AutoPickAdoptionCard data={autoPick} />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <CoverageBars
                    title="Transferable Skills (all teachers)"
                    data={coverage?.by_transferable_skill}
                    total={total}
                    testid="admin-cov-transferable"
                  />
                  <CoverageBars
                    title="Ontario Learning Skills (all teachers)"
                    data={coverage?.by_learning_skill}
                    total={total}
                    testid="admin-cov-learning"
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <CoverageBars
                    title="Subjects"
                    data={coverage?.by_subject}
                    total={total}
                    testid="admin-cov-subjects"
                  />
                  <CoverageBars
                    title="Grades"
                    data={coverage?.by_grade}
                    total={total}
                    testid="admin-cov-grades"
                  />
                  <CoverageBars
                    title="Activity types"
                    data={coverage?.by_activity_type}
                    total={total}
                    testid="admin-cov-activity-type"
                  />
                </div>

                <CoverageBars
                  title="Strands (all teachers)"
                  data={coverage?.by_strand}
                  total={total}
                  testid="admin-cov-strands"
                />

                <CoverageBars
                  title="Ontario curriculum expectations covered (all teachers)"
                  data={coverage?.by_curriculum}
                  total={total}
                  testid="admin-cov-curriculum"
                />
              </TabsContent>

              <TabsContent value="email" className="space-y-4" data-testid="tab-content-email">
                <AdminEmailBlastPanel />
              </TabsContent>

              <TabsContent value="retention" className="space-y-4" data-testid="tab-content-retention">
                <RetentionUsagePanel />
              </TabsContent>

              <TabsContent value="posters" className="space-y-4" data-testid="tab-content-posters">
                <PostersFunnelPanel />
              </TabsContent>

              <TabsContent value="reflection" className="space-y-4" data-testid="tab-content-reflection">
                <ReflectionAggregatePanel />
              </TabsContent>

              <TabsContent value="llm-usage" className="space-y-4" data-testid="tab-content-llm-usage">
                <LlmUsagePanel />
              </TabsContent>

              <TabsContent value="homepage" className="space-y-4" data-testid="tab-content-homepage">
                <HomepageHeroPanel />
                <ReportCardNudgePanel />
              </TabsContent>

              <TabsContent value="featured-lesson" className="space-y-4" data-testid="tab-content-featured-lesson">
                <FeaturedLessonPanel />
              </TabsContent>

              <TabsContent value="maintenance" className="space-y-4" data-testid="tab-content-maintenance">
                <PackageCachePanel />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>

      <NudgeComposer
        open={!!nudgeGap}
        onOpenChange={(v) => !v && setNudgeGap(null)}
        gapName={nudgeGap}
      />
    </div>
  );
};

export default Admin;


// Inline toggle button used in the Signed-up Teachers table to grant /
// revoke Math Engine access. Used for early-access onboarding before
// the Pro-tier Stripe subscription goes live.
const MathAccessToggle = ({ email, granted, onChange }) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      const next = !granted;
      const res = await adminGrantMathAccess(email, next);
      const newVal = !!res?.user?.has_math_engine_access;
      onChange?.(newVal);
      toast.success(
        newVal ? `Math Engine granted to ${email}` : `Math Engine revoked from ${email}`,
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Could not update access";
      toast.error(String(detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      data-testid={`math-access-toggle-${email}`}
      className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-extrabold uppercase tracking-[0.1em] transition disabled:opacity-50 ${
        granted
          ? "bg-[#96ded1]/40 text-[#1c2d3d] hover:bg-[#96ded1]/60"
          : "bg-white border border-[#4a6984]/25 text-[#4a6984] hover:bg-[#f4f5f7]"
      }`}
    >
      {busy ? "…" : granted ? "Granted · revoke" : "Grant"}
    </button>
  );
};


// Tier 2 #4 (Feb 2026) — Auto-pick adoption card.
//
// Renders adoption % + the top skills the LLM picked when a teacher
// chose "Let Atomic Edu choose". Helps spot:
//   - whether teachers are starting to default to auto
//   - which skill Claude picks too often (a sign the prompt biases
//     toward one of the seven)
//   - which grade/subject combos lean hardest on auto-pick
const AutoPickAdoptionCard = ({ data }) => {
  if (!data) return null;
  const total = Number(data.total_auto_picks || 0);
  const adoption = Number(data.adoption_pct || 0);
  const skillRows = Object.entries(data.by_picked_skill || {}).sort(
    (a, b) => b[1] - a[1],
  );
  const gradeRows = Object.entries(data.by_grade || {}).sort(
    (a, b) => b[1] - a[1],
  );
  const subjectRows = Object.entries(data.by_subject || {}).sort(
    (a, b) => b[1] - a[1],
  );
  const comboRows = Object.entries(data.by_grade_subject || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);
  return (
    <div
      className="ae-card p-6 md:p-7 border-[#96ded1]/60 bg-[#96ded1]/10"
      data-testid="admin-auto-pick-card"
    >
      <div className="flex items-start gap-3 mb-5">
        <div className="h-9 w-9 rounded-lg bg-white border border-[#96ded1] grid place-items-center shrink-0">
          <Wand2 className="h-4 w-4 text-[#1c2d3d]" />
        </div>
        <div>
          <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
            Auto-pick adoption — Let Atomic Edu choose
          </h3>
          <p className="text-sm text-[#4a6984]/75">
            What teachers picked when they let us choose the transferable
            skill. Use this to spot whether one skill is over-represented
            and which grade + subject combos lean hardest on auto.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div
          className="rounded-lg bg-white border border-[#96ded1]/50 px-4 py-3"
          data-testid="admin-auto-pick-total"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4a6984]/70">
            Auto picks
          </p>
          <p className="font-black text-2xl text-[#1c2d3d]">{total}</p>
        </div>
        <div
          className="rounded-lg bg-white border border-[#96ded1]/50 px-4 py-3"
          data-testid="admin-auto-pick-adoption"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4a6984]/70">
            Adoption
          </p>
          <p className="font-black text-2xl text-[#1c2d3d]">{adoption}%</p>
          <p className="text-[11px] text-[#4a6984]/70 mt-0.5">
            of all activities
          </p>
        </div>
        <div className="rounded-lg bg-white border border-[#96ded1]/50 px-4 py-3 col-span-2 md:col-span-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4a6984]/70">
            Top pick
          </p>
          <p className="font-extrabold text-base text-[#1c2d3d] truncate">
            {skillRows[0]?.[0] || "—"}
          </p>
          <p className="text-[11px] text-[#4a6984]/70 mt-0.5">
            {skillRows[0]?.[1] || 0} of {total}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CoverageBars
          title="Skill Atomic Edu picked"
          data={data.by_picked_skill}
          total={total}
          testid="admin-auto-pick-skill"
        />
        <CoverageBars
          title="Grade + subject combos (top 8)"
          data={Object.fromEntries(comboRows)}
          total={total}
          testid="admin-auto-pick-combo"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <CoverageBars
          title="Grade (auto-pick only)"
          data={data.by_grade}
          total={total}
          testid="admin-auto-pick-grade"
        />
        <CoverageBars
          title="Subject (auto-pick only)"
          data={data.by_subject}
          total={total}
          testid="admin-auto-pick-subject"
        />
      </div>

      {total === 0 && (
        <p className="text-sm text-[#4a6984]/70 mt-4">
          Once a teacher generates a lesson with “Let Atomic Edu choose”,
          its picked skill will show up here.
        </p>
      )}
      {/* Silence linter for unused destructure when total === 0. */}
      <span hidden>{gradeRows.length + subjectRows.length}</span>
    </div>
  );
};



// ---------------------------------------------------------------------------
// Teachers tab — full list + permanent, reversible user-deletion tool.
// ---------------------------------------------------------------------------
const SUB_STATES = [
  { v: "", label: "Any subscription" },
  { v: "paid", label: "Paid" },
  { v: "active", label: "Active" },
  { v: "trialing", label: "Trialing" },
  { v: "past_due", label: "Past due" },
  { v: "canceled", label: "Canceled" },
  { v: "none", label: "No subscription" },
];

const selectCls =
  "rounded-md border border-[#4a6984]/25 bg-white text-[#4a6984] text-sm font-semibold px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-[#96ded1]";

function TeachersTab({ isSuperAdmin }) {
  const [view, setView] = useState("active"); // active | deleted
  const [data, setData] = useState(null);
  const [deleted, setDeleted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    signup_from: "",
    signup_to: "",
    sub_state: "",
    has_stripe: "",
    activity: "",
    sort: "signup_desc",
  });
  const [selected, setSelected] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // {ids, preview} | null
  const [typedCount, setTypedCount] = useState("");
  const [busy, setBusy] = useState(false);

  const loadActive = async (f = filters) => {
    setLoading(true);
    try {
      const d = await adminTeachers(f);
      setData(d);
      setSelected(new Set());
    } catch (e) {
      toast.error("Could not load teachers.");
    } finally {
      setLoading(false);
    }
  };

  const loadDeleted = async () => {
    setLoading(true);
    try {
      setDeleted(await adminRecentlyDeletedUsers());
      setSelected(new Set());
    } catch (e) {
      toast.error("Could not load recently deleted.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view === "active") loadActive();
    else loadDeleted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const rows = data?.teachers || [];
  const selectableIds = useMemo(
    () => rows.filter((r) => !r.delete_blocked_local).map((r) => r.user_id),
    [rows]
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggle = (id) => {
    setSelected((cur) => {
      const n = new Set(cur);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  };

  const applyFilters = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    loadActive(next);
  };

  // ---- Bulk delete flow ----
  const openConfirm = async (ids) => {
    if (!ids.length) return;
    setTypedCount("");
    setBusy(true);
    try {
      const preview = await adminPreviewDeleteUsers(ids);
      setConfirm({ ids, preview });
    } catch (e) {
      toast.error("Could not verify the selection.");
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const res = await adminDeleteUsers(confirm.ids);
      toast.success(
        `Deleted ${res.deleted_count} teacher${res.deleted_count === 1 ? "" : "s"}.` +
          (res.skipped.length ? ` ${res.skipped.length} skipped (guarded).` : "")
      );
      setConfirm(null);
      await loadActive();
    } catch (e) {
      toast.error("Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  // ---- Recently-deleted actions ----
  const restore = async (ids) => {
    setBusy(true);
    try {
      const res = await adminRestoreUsers(ids);
      toast.success(`Restored ${res.restored_count}.`);
      await loadDeleted();
    } catch (e) {
      toast.error("Restore failed.");
    } finally {
      setBusy(false);
    }
  };

  const purgeNow = async (ids) => {
    if (!window.confirm(
      `Permanently purge ${ids.length} account${ids.length === 1 ? "" : "s"} now? ` +
      "This skips the 30-day window and cannot be undone."
    )) return;
    setBusy(true);
    try {
      const res = await adminPurgeUsersNow(ids);
      toast.success(`Purged ${res.purged_count} permanently.`);
      await loadDeleted();
    } catch (e) {
      toast.error("Purge failed.");
    } finally {
      setBusy(false);
    }
  };

  const th =
    "text-left font-extrabold uppercase tracking-[0.12em] text-xs text-[#4a6984] p-3 border-b border-[#4a6984]/15";
  const td = "p-3 border-b border-[#4a6984]/10";

  return (
    <TooltipProvider>
      <div className="ae-card p-6 md:p-7" data-testid="admin-teachers-card">
        {/* View switch */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="teachers-view-active"
              onClick={() => setView("active")}
              className={`px-3.5 py-2 rounded-md text-sm font-extrabold transition-colors ${
                view === "active"
                  ? "bg-[#1c2d3d] text-white"
                  : "bg-white border border-[#4a6984]/20 text-[#4a6984] hover:bg-[#f4f5f7]"
              }`}
            >
              Signed-up teachers ({view === "active" ? data?.count ?? 0 : "…"})
            </button>
            <button
              type="button"
              data-testid="teachers-view-deleted"
              onClick={() => setView("deleted")}
              className={`px-3.5 py-2 rounded-md text-sm font-extrabold transition-colors ${
                view === "deleted"
                  ? "bg-[#1c2d3d] text-white"
                  : "bg-white border border-[#4a6984]/20 text-[#4a6984] hover:bg-[#f4f5f7]"
              }`}
            >
              Recently deleted ({view === "deleted" ? deleted?.count ?? 0 : "…"})
            </button>
          </div>
          {view === "active" && (
            <a
              href={adminTeachersCsvUrl(filters)}
              data-testid="admin-teachers-csv"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] font-semibold text-sm hover:bg-[#f4f5f7] hover:border-[#4a6984]/35 transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Download CSV
            </a>
          )}
        </div>

        {/* ---------- ACTIVE VIEW ---------- */}
        {view === "active" && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="teachers-filters">
              <label className="flex flex-col gap-1 text-[11px] font-bold uppercase tracking-wide text-[#4a6984]/70">
                Signed up from
                <input
                  type="date"
                  data-testid="filter-signup-from"
                  className={selectCls}
                  value={filters.signup_from}
                  onChange={(e) => applyFilters({ signup_from: e.target.value })}
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-bold uppercase tracking-wide text-[#4a6984]/70">
                To
                <input
                  type="date"
                  data-testid="filter-signup-to"
                  className={selectCls}
                  value={filters.signup_to}
                  onChange={(e) => applyFilters({ signup_to: e.target.value })}
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-bold uppercase tracking-wide text-[#4a6984]/70">
                Subscription
                <select
                  data-testid="filter-sub-state"
                  className={selectCls}
                  value={filters.sub_state}
                  onChange={(e) => applyFilters({ sub_state: e.target.value })}
                >
                  {SUB_STATES.map((s) => (
                    <option key={s.v} value={s.v}>{s.label}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-bold uppercase tracking-wide text-[#4a6984]/70">
                Stripe
                <select
                  data-testid="filter-has-stripe"
                  className={selectCls}
                  value={filters.has_stripe}
                  onChange={(e) => applyFilters({ has_stripe: e.target.value })}
                >
                  <option value="">Any</option>
                  <option value="yes">Has Stripe</option>
                  <option value="no">No Stripe</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-bold uppercase tracking-wide text-[#4a6984]/70">
                Activity
                <select
                  data-testid="filter-activity"
                  className={selectCls}
                  value={filters.activity}
                  onChange={(e) => applyFilters({ activity: e.target.value })}
                >
                  <option value="">Any</option>
                  <option value="zero">No lessons</option>
                  <option value="nonzero">Has lessons</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-bold uppercase tracking-wide text-[#4a6984]/70">
                Sort
                <select
                  data-testid="filter-sort"
                  className={selectCls}
                  value={filters.sort}
                  onChange={(e) => applyFilters({ sort: e.target.value })}
                >
                  <option value="signup_desc">Newest first</option>
                  <option value="signup_asc">Oldest first</option>
                </select>
              </label>
              <div className="flex items-center gap-2 text-xs text-[#4a6984]/50 pb-2">
                <Filter className="h-3.5 w-3.5" />
                {rows.length} shown
              </div>
            </div>

            {/* Bulk bar */}
            {selected.size > 0 && (
              <div
                className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 rounded-md bg-[#1c2d3d] text-white"
                data-testid="teachers-bulk-bar"
              >
                <span className="font-bold text-sm">
                  {selected.size} selected
                </span>
                <button
                  type="button"
                  data-testid="bulk-delete-btn"
                  disabled={busy}
                  onClick={() => openConfirm([...selected])}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-md bg-[#e2544a] text-white font-extrabold text-sm hover:bg-[#c9463d] transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete selected
                </button>
              </div>
            )}

            {loading ? (
              <p className="text-sm text-[#4a6984]/60">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-[#4a6984]/60">No teachers match these filters.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-[#4a6984]/10">
                <table className="w-full text-sm">
                  <thead className="bg-[#f4f5f7]">
                    <tr>
                      <th className={`${th} w-10`}>
                        <Checkbox
                          data-testid="select-all-teachers"
                          checked={allSelected}
                          onCheckedChange={toggleAll}
                          aria-label="Select all"
                        />
                      </th>
                      <th className={th}>Email</th>
                      <th className={th}>Name</th>
                      <th className={th}>Tier</th>
                      <th className={th}>Lessons</th>
                      <th className={th}>Signed up</th>
                      <th className={th}>Math</th>
                      <th className={`${th} text-right`}>Delete</th>
                    </tr>
                  </thead>
                  <tbody data-testid="admin-teachers-rows">
                    {rows.map((t, idx) => {
                      const tierLabel = t.subscription_tier || "free";
                      const isPaid = ["basic", "standard", "pro", "pro_plus"].includes(tierLabel);
                      const blocked = !!t.delete_blocked_local;
                      const isSel = selected.has(t.user_id);
                      return (
                        <tr key={`t-${idx}-${t.user_id || t.email || ""}`} className={isSel ? "bg-[#96ded1]/10" : ""}>
                          <td className={td}>
                            <Checkbox
                              data-testid={`select-teacher-${t.user_id}`}
                              checked={isSel}
                              disabled={blocked}
                              onCheckedChange={() => toggle(t.user_id)}
                              aria-label={`Select ${t.email}`}
                            />
                          </td>
                          <td className={`${td} font-semibold text-[#4a6984]`}>{t.email}</td>
                          <td className={`${td} text-[#4a6984]/85`}>{t.name || "—"}</td>
                          <td className={td}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-[0.12em] ${
                                  isPaid ? "bg-[#96ded1]/35 text-[#1c2d3d]" : "bg-[#4a6984]/10 text-[#4a6984]"
                                }`}
                              >
                                {tierLabel}
                                {t.subscription_status && t.subscription_status !== "active" && (
                                  <span className="ml-1 font-semibold normal-case tracking-normal">
                                    · {t.subscription_status}
                                  </span>
                                )}
                              </span>
                              {isSuperAdmin && (
                                <TierOverrideDropdown
                                  email={t.email}
                                  currentTier={t.subscription_tier}
                                  currentStatus={t.subscription_status}
                                  onChange={() => loadActive()}
                                />
                              )}
                            </div>
                          </td>
                          <td className={`${td} text-[#4a6984]/85 tabular-nums`}>{t.lesson_count ?? 0}</td>
                          <td className={`${td} text-[#4a6984]/70`}>{fmtDate(t.created_at)}</td>
                          <td className={td}>
                            <MathAccessToggle
                              email={t.email}
                              granted={!!t.has_math_engine_access}
                              onChange={() => loadActive()}
                            />
                          </td>
                          <td className={`${td} text-right`}>
                            {blocked ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    data-testid={`delete-blocked-${t.user_id}`}
                                    className="inline-flex items-center gap-1 text-[#4a6984]/40 cursor-not-allowed"
                                  >
                                    <Lock className="h-4 w-4" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Active subscription — cancel in Stripe first
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <button
                                type="button"
                                data-testid={`delete-teacher-${t.user_id}`}
                                onClick={() => openConfirm([t.user_id])}
                                className="inline-flex items-center gap-1 text-[#e2544a] hover:text-[#c9463d] font-semibold"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ---------- RECENTLY DELETED VIEW ---------- */}
        {view === "deleted" && (
          <>
            <p className="text-sm text-[#4a6984]/70 mb-4">
              Soft-deleted accounts. They are hidden everywhere and cannot sign in.
              After 30 days they are permanently purged. Restore brings the full
              account back; purge now removes it immediately.
            </p>
            {selected.size > 0 && (
              <div
                className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 rounded-md bg-[#1c2d3d] text-white"
                data-testid="deleted-bulk-bar"
              >
                <span className="font-bold text-sm">{selected.size} selected</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="bulk-restore-btn"
                    disabled={busy}
                    onClick={() => restore([...selected])}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-md bg-[#96ded1] text-[#1c2d3d] font-extrabold text-sm hover:bg-[#7ecfc0] transition-colors disabled:opacity-50"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </button>
                  <button
                    type="button"
                    data-testid="bulk-purge-btn"
                    disabled={busy}
                    onClick={() => purgeNow([...selected])}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-md bg-[#e2544a] text-white font-extrabold text-sm hover:bg-[#c9463d] transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Purge now
                  </button>
                </div>
              </div>
            )}
            {loading ? (
              <p className="text-sm text-[#4a6984]/60">Loading…</p>
            ) : (deleted?.users || []).length === 0 ? (
              <p className="text-sm text-[#4a6984]/60">Nothing has been deleted recently.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-[#4a6984]/10">
                <table className="w-full text-sm">
                  <thead className="bg-[#f4f5f7]">
                    <tr>
                      <th className={`${th} w-10`}>
                        <Checkbox
                          data-testid="select-all-deleted"
                          checked={
                            (deleted?.users || []).length > 0 &&
                            (deleted?.users || []).every((u) => selected.has(u.user_id))
                          }
                          onCheckedChange={() => {
                            const ids = (deleted?.users || []).map((u) => u.user_id);
                            const all = ids.every((id) => selected.has(id));
                            setSelected(all ? new Set() : new Set(ids));
                          }}
                          aria-label="Select all"
                        />
                      </th>
                      <th className={th}>Email</th>
                      <th className={th}>Signed up</th>
                      <th className={th}>Deleted</th>
                      <th className={th}>Days left</th>
                      <th className={`${th} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody data-testid="admin-deleted-rows">
                    {(deleted?.users || []).map((u, idx) => (
                      <tr key={`d-${idx}-${u.user_id || u.email || ""}`} className={selected.has(u.user_id) ? "bg-[#96ded1]/10" : ""}>
                        <td className={td}>
                          <Checkbox
                            data-testid={`select-deleted-${u.user_id}`}
                            checked={selected.has(u.user_id)}
                            onCheckedChange={() => toggle(u.user_id)}
                            aria-label={`Select ${u.email}`}
                          />
                        </td>
                        <td className={`${td} font-semibold text-[#4a6984]`}>{u.email}</td>
                        <td className={`${td} text-[#4a6984]/70`}>{fmtDate(u.signup_date)}</td>
                        <td className={`${td} text-[#4a6984]/70`}>{fmtDate(u.deleted_at)}</td>
                        <td className={`${td} tabular-nums font-bold text-[#4a6984]`}>
                          {u.days_remaining}
                        </td>
                        <td className={`${td} text-right`}>
                          <div className="inline-flex items-center gap-3">
                            <button
                              type="button"
                              data-testid={`restore-${u.user_id}`}
                              disabled={busy}
                              onClick={() => restore([u.user_id])}
                              className="inline-flex items-center gap-1 text-[#4a6984] hover:text-[#1c2d3d] font-semibold disabled:opacity-50"
                            >
                              <RotateCcw className="h-4 w-4" /> Restore
                            </button>
                            <button
                              type="button"
                              data-testid={`purge-${u.user_id}`}
                              disabled={busy}
                              onClick={() => purgeNow([u.user_id])}
                              className="inline-flex items-center gap-1 text-[#e2544a] hover:text-[#c9463d] font-semibold disabled:opacity-50"
                            >
                              <Trash2 className="h-4 w-4" /> Purge
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bulk delete confirmation */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="delete-confirm-dialog" className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1c2d3d]">
              <AlertTriangle className="h-5 w-5 text-[#e2544a]" />
              Delete {confirm?.preview?.deletable_count ?? 0} teacher
              {(confirm?.preview?.deletable_count ?? 0) === 1 ? "" : "s"}?
            </DialogTitle>
          </DialogHeader>
          {confirm && (
            <div className="space-y-4 text-sm text-[#4a6984]">
              <p>
                This soft-deletes the account. It is hidden everywhere and cannot sign in.
                You can restore it for 30 days, after which it is permanently purged with
                all its data.
              </p>
              <p
                className="text-xs font-semibold text-[#4a6984]/70"
                data-testid="verification-source"
              >
                Verified via:{" "}
                {confirm.preview.verification_source === "live_stripe"
                  ? "LIVE Stripe"
                  : "local record — Stripe not live in this environment"}
              </p>
              {confirm.preview.first_ten_emails?.length > 0 && (
                <div className="rounded-md bg-[#f4f5f7] p-3 max-h-40 overflow-y-auto">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#4a6984]/60 mb-1">
                    First {confirm.preview.first_ten_emails.length} of{" "}
                    {confirm.preview.found}
                  </p>
                  {confirm.preview.first_ten_emails.map((e, i) => (
                    <div key={`${e}-${i}`} className="text-[#4a6984]/85">{e}</div>
                  ))}
                </div>
              )}
              {confirm.preview.blocked_count > 0 && (
                <div
                  className="rounded-md border border-[#e2544a]/30 bg-[#e2544a]/5 p-3"
                  data-testid="delete-blocked-notice"
                >
                  <p className="font-bold text-[#c9463d]">
                    {confirm.preview.blocked_count} will be skipped (active subscription).
                  </p>
                  <p className="text-xs text-[#4a6984]/70 mt-0.5">
                    Cancel these in Stripe first. Only the{" "}
                    {confirm.preview.deletable_count} deletable account
                    {confirm.preview.deletable_count === 1 ? "" : "s"} will be removed.
                  </p>
                </div>
              )}
              {confirm.preview.requires_typed_count && (
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wide text-[#4a6984]/70">
                    Type {confirm.preview.deletable_count} to confirm
                  </span>
                  <Input
                    data-testid="typed-count-input"
                    value={typedCount}
                    onChange={(e) => setTypedCount(e.target.value)}
                    className="mt-1"
                    inputMode="numeric"
                  />
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className="px-4 py-2 rounded-md border border-[#4a6984]/25 text-[#4a6984] font-semibold text-sm hover:bg-[#f4f5f7]"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="confirm-delete-btn"
              disabled={
                busy ||
                (confirm?.preview?.deletable_count ?? 0) === 0 ||
                (confirm?.preview?.requires_typed_count &&
                  typedCount.trim() !== String(confirm.preview.deletable_count))
              }
              onClick={runDelete}
              className="px-4 py-2 rounded-md bg-[#e2544a] text-white font-extrabold text-sm hover:bg-[#c9463d] transition-colors disabled:opacity-50"
            >
              {busy ? "Deleting…" : `Delete ${confirm?.preview?.deletable_count ?? 0}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
